import React, { useState, useCallback } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Paper,
  FormControl,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Card,
  CardContent,
  Grid,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  Checkbox,
  Tooltip,
  IconButton,
  Divider,
  AlertTitle,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CheckCircle as SuccessIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  Info as InfoIcon,
  Sync as SyncIcon,
  Description as FileIcon,
  SelectAll as SelectAllIcon,
  DeselectOutlined as DeselectIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { importApi } from '../../services/api';

// Steps dinamici in base alla modalità
const stepsManual = [
  'Seleziona tipo dati',
  'Carica file CSV',
  'Anteprima e validazione',
  'Esecuzione importazione',
];

const stepsExported = [
  'Carica file CSV esportato',
  'Anteprima e validazione',
  'Esecuzione importazione',
];

// Informazioni sulle entità
const entityInfo = {
  customers: {
    label: 'Clienti',
    description: 'Anagrafica clienti con dati fiscali e contatti',
    icon: '👥',
    color: '#1976d2',
  },
  invoices: {
    label: 'Fatture',
    description: 'Fatture emesse con importi e scadenze',
    icon: '📄',
    color: '#388e3c',
  },
  meters: {
    label: 'Contatori',
    description: 'Contatori gas associati a clienti e impianti',
    icon: '⏱️',
    color: '#f57c00',
  },
  plants: {
    label: 'Impianti',
    description: 'Impianti GPL con serbatoi e capacità',
    icon: '🏭',
    color: '#7b1fa2',
  },
  contracts: {
    label: 'Contratti',
    description: 'Contratti di fornitura con prezzi e condizioni',
    icon: '📋',
    color: '#0288d1',
  },
  advances: {
    label: 'Acconti',
    description: 'Acconti e pagamenti anticipati',
    icon: '💰',
    color: '#ed6c02',
  },
};

export default function ImportWizard({ onComplete, onCancel }) {
  // Stato per la modalità di import
  const [importSource, setImportSource] = useState(null); // 'manual' | 'exported'
  
  const [activeStep, setActiveStep] = useState(0);
  const [selectedEntity, setSelectedEntity] = useState('');
  const [entities, setEntities] = useState([]);
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [importMode, setImportMode] = useState('skip');
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Nuovo: selezione righe per import selettivo
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectAllMode, setSelectAllMode] = useState(true);
  
  // Stato per auto-detection
  const [detectedEntity, setDetectedEntity] = useState(null);
  const [detecting, setDetecting] = useState(false);

  // Carica lista entità all'avvio
  React.useEffect(() => {
    const loadEntities = async () => {
      try {
        const response = await importApi.getEntities();
        setEntities(response.data.data || []);
      } catch (err) {
        setError('Errore nel caricamento delle entità');
      }
    };
    loadEntities();
  }, []);

  // Auto-detect tipo CSV
  const handleAutoDetect = async (uploadedFile) => {
    setDetecting(true);
    setError(null);
    
    try {
      const response = await importApi.detect(uploadedFile || file);
      const data = response.data.data;
      
      if (data.detected) {
        setDetectedEntity(data.entity);
        setSelectedEntity(data.entity);
      } else {
        setError('Tipo CSV non riconosciuto automaticamente. Prova l\'import manuale selezionando il tipo.');
        setDetectedEntity(null);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nel rilevamento del tipo CSV');
    } finally {
      setDetecting(false);
    }
  };

  // Dropzone per upload file
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0]);
      setError(null);
      setDetectedEntity(null);
      
      // Se siamo in modalità "exported", auto-detect immediato
      if (importSource === 'exported') {
        handleAutoDetect(acceptedFiles[0]);
      }
    }
  }, [importSource]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
    },
    multiple: false,
  });

  // Download template
  const handleDownloadTemplate = async () => {
    try {
      const response = await importApi.downloadTemplate(selectedEntity);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedEntity}_template.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Errore nel download del template');
    }
  };

  // Preview importazione
  const handlePreview = async () => {
    if (!file || !selectedEntity) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await importApi.preview(selectedEntity, file);
      setPreviewData(response.data.data);
      
      // Inizializza selezione: tutte le righe valide selezionate per default
      const validRowNumbers = response.data.data.preview
        .filter(item => !item.errors || item.errors.length === 0)
        .map(item => item.row);
      setSelectedRows(new Set(validRowNumbers));
      setSelectAllMode(true);
      
      // Avanza allo step preview
      if (importSource === 'exported') {
        setActiveStep(1);
      } else {
        setActiveStep(2);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nella validazione del file');
    } finally {
      setLoading(false);
    }
  };

  // Esegui importazione
  const handleExecute = async () => {
    if (!file || !selectedEntity) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Passa le righe selezionate se import selettivo
      const rowsToImport = selectedRows.size > 0 ? Array.from(selectedRows) : null;
      const response = await importApi.execute(selectedEntity, file, importMode, rowsToImport);
      setImportResult(response.data.data);
      
      // Avanza allo step risultato
      if (importSource === 'exported') {
        setActiveStep(2);
      } else {
        setActiveStep(3);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Errore durante l\'importazione');
    } finally {
      setLoading(false);
    }
  };

  // Reset wizard
  const handleReset = () => {
    setImportSource(null);
    setActiveStep(0);
    setSelectedEntity('');
    setFile(null);
    setPreviewData(null);
    setImportResult(null);
    setError(null);
    setSelectedRows(new Set());
    setDetectedEntity(null);
  };

  // Navigazione
  const handleNext = () => {
    if (importSource === 'exported') {
      // Modalità exported: step 0 = upload+detect, step 1 = preview, step 2 = risultato
      if (activeStep === 0) {
        handlePreview();
      } else if (activeStep === 1) {
        handleExecute();
      }
    } else {
      // Modalità manuale: step 0 = selezione entità, step 1 = upload, step 2 = preview, step 3 = risultato
      if (activeStep === 1) {
        handlePreview();
      } else if (activeStep === 2) {
        handleExecute();
      } else {
        setActiveStep((prev) => prev + 1);
      }
    }
  };

  const handleBack = () => {
    if (importSource === 'exported') {
      if (activeStep === 0) {
        // Torna alla selezione modalità
        setImportSource(null);
        setFile(null);
        setDetectedEntity(null);
      } else {
        setActiveStep((prev) => prev - 1);
      }
    } else {
      if (activeStep === 0) {
        // Torna alla selezione modalità
        setImportSource(null);
      } else {
        setActiveStep((prev) => prev - 1);
      }
    }
    setError(null);
  };

  // Toggle selezione riga
  const toggleRowSelection = (rowNumber) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(rowNumber)) {
      newSelected.delete(rowNumber);
    } else {
      newSelected.add(rowNumber);
    }
    setSelectedRows(newSelected);
  };

  // Seleziona/deseleziona tutte le righe valide
  const toggleSelectAll = () => {
    if (selectAllMode) {
      // Deseleziona tutto
      setSelectedRows(new Set());
      setSelectAllMode(false);
    } else {
      // Seleziona tutte le righe valide
      const validRowNumbers = previewData.preview
        .filter(item => !item.errors || item.errors.length === 0)
        .map(item => item.row);
      setSelectedRows(new Set(validRowNumbers));
      setSelectAllMode(true);
    }
  };

  // Può proseguire?
  const canProceed = () => {
    if (importSource === 'exported') {
      switch (activeStep) {
        case 0:
          return !!file && !!selectedEntity && !detecting;
        case 1:
          return previewData && selectedRows.size > 0;
        default:
          return false;
      }
    } else {
      switch (activeStep) {
        case 0:
          return !!selectedEntity;
        case 1:
          return !!file;
        case 2:
          return previewData && selectedRows.size > 0;
        default:
          return false;
      }
    }
  };

  // Renderizza selezione modalità import
  const renderModeSelection = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h5" gutterBottom sx={{ textAlign: 'center', mb: 3 }}>
        Come vuoi importare i dati?
      </Typography>
      
      <Grid container spacing={3} justifyContent="center">
        <Grid item xs={12} md={5}>
          <Card
            sx={{
              cursor: 'pointer',
              border: 2,
              borderColor: 'transparent',
              transition: 'all 0.3s',
              '&:hover': {
                borderColor: 'primary.main',
                transform: 'translateY(-4px)',
                boxShadow: 4,
              },
            }}
            onClick={() => setImportSource('manual')}
          >
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <FileIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                📥 Import Manuale (Firefox CRM)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Importa dati dal vecchio CRM Firefox. Seleziona il tipo di dati e carica un file CSV con il formato specifico.
              </Typography>
              <Chip label="Per migrazione iniziale" size="small" color="default" />
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={5}>
          <Card
            sx={{
              cursor: 'pointer',
              border: 2,
              borderColor: 'transparent',
              transition: 'all 0.3s',
              '&:hover': {
                borderColor: 'success.main',
                transform: 'translateY(-4px)',
                boxShadow: 4,
              },
            }}
            onClick={() => setImportSource('exported')}
          >
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <SyncIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                🔄 Reimporta CSV Esportato
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Reimporta dati esportati da questo CRM con "Esportazione Dati CSV". Utile per trasferire dati tra aziende.
              </Typography>
              <Chip label="Auto-detection tipo" size="small" color="success" />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      <Alert severity="info" sx={{ mt: 4 }} icon={<InfoIcon />}>
        <AlertTitle>Trasferimento dati tra aziende</AlertTitle>
        <Typography variant="body2">
          Per trasferire dati da un'azienda all'altra:
        </Typography>
        <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>
          <li>Nell'azienda sorgente: vai su <strong>Impostazioni → Esportazione Dati CSV</strong> e scarica i file</li>
          <li>Cambia azienda dalla selezione in alto</li>
          <li>Usa <strong>"Reimporta CSV Esportato"</strong> per caricare i file nell'azienda di destinazione</li>
        </ol>
      </Alert>
    </Box>
  );

  // Step 0: Selezione entità (solo per import manuale)
  const renderEntitySelection = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Seleziona il tipo di dati da importare
      </Typography>
      
      <Grid container spacing={2} sx={{ mt: 1 }}>
        {entities.map((entity) => {
          const info = entityInfo[entity.id] || {
            label: entity.name,
            description: '',
            icon: '📁',
            color: '#666',
          };
          
          return (
            <Grid item xs={12} sm={6} md={4} key={entity.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  border: selectedEntity === entity.id ? 3 : 1,
                  borderColor: selectedEntity === entity.id ? info.color : 'divider',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: info.color,
                    transform: 'translateY(-2px)',
                    boxShadow: 3,
                  },
                }}
                onClick={() => setSelectedEntity(entity.id)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h4" sx={{ mr: 1 }}>
                      {info.icon}
                    </Typography>
                    <Typography variant="h6">{info.label}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {info.description}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Campi richiesti:{' '}
                      <strong>{entity.requiredFields.join(', ')}</strong>
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );

  // Step Upload: Upload file (per entrambe le modalità)
  const renderFileUpload = () => (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          {importSource === 'exported' 
            ? 'Carica file CSV esportato dal CRM'
            : `Carica file CSV per ${entityInfo[selectedEntity]?.label || selectedEntity}`
          }
        </Typography>
        {importSource === 'manual' && selectedEntity && (
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
          >
            Scarica Template
          </Button>
        )}
      </Box>

      <Paper
        {...getRootProps()}
        sx={{
          p: 4,
          textAlign: 'center',
          backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'divider',
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          },
        }}
      >
        <input {...getInputProps()} />
        <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        {isDragActive ? (
          <Typography>Rilascia il file qui...</Typography>
        ) : (
          <>
            <Typography variant="h6" gutterBottom>
              Trascina qui il file CSV
            </Typography>
            <Typography variant="body2" color="text.secondary">
              oppure clicca per selezionare
            </Typography>
          </>
        )}
      </Paper>

      {file && (
        <Alert severity="success" sx={{ mt: 2 }} icon={<SuccessIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="subtitle1">{file.name}</Typography>
              <Typography variant="body2">
                {(file.size / 1024).toFixed(2)} KB
              </Typography>
            </Box>
            <Button
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setFile(null);
                setDetectedEntity(null);
              }}
              color="inherit"
            >
              Rimuovi
            </Button>
          </Box>
        </Alert>
      )}

      {/* Auto-detection per modalità exported */}
      {importSource === 'exported' && file && (
        <Box sx={{ mt: 2 }}>
          {detecting ? (
            <Alert severity="info" icon={<SyncIcon sx={{ animation: 'spin 1s linear infinite' }} />}>
              Rilevamento automatico del tipo di dati...
            </Alert>
          ) : detectedEntity ? (
            <Alert severity="success">
              <AlertTitle>Tipo rilevato automaticamente</AlertTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h5">{entityInfo[detectedEntity]?.icon}</Typography>
                <Typography variant="subtitle1">
                  <strong>{entityInfo[detectedEntity]?.label || detectedEntity}</strong>
                </Typography>
              </Box>
            </Alert>
          ) : null}
        </Box>
      )}

      <Alert severity="info" sx={{ mt: 2 }} icon={<InfoIcon />}>
        <Typography variant="body2">
          {importSource === 'exported' ? (
            <>
              Il sistema riconosce automaticamente il tipo di dati dal file CSV esportato.
              Sono supportati: Clienti, Fatture, Contatori, Impianti, Acconti.
            </>
          ) : (
            <>
              Il file CSV deve avere il separatore <strong>;</strong> (punto e virgola).
              La prima riga deve contenere i nomi delle colonne.
            </>
          )}
        </Typography>
      </Alert>
    </Box>
  );

  // Step 2: Preview con selezione righe
  const renderPreview = () => (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Anteprima importazione - {entityInfo[selectedEntity]?.label || selectedEntity}
        </Typography>
        <Chip 
          icon={entityInfo[selectedEntity]?.icon ? <span>{entityInfo[selectedEntity].icon}</span> : undefined}
          label={entityInfo[selectedEntity]?.label}
          color="primary"
        />
      </Box>

      {previewData && (
        <>
          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4">{previewData.totalRows}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Righe totali
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
                <Typography variant="h4" color="success.dark">
                  {previewData.newRows}
                </Typography>
                <Typography variant="body2" color="success.dark">
                  Nuovi record
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light' }}>
                <Typography variant="h4" color="warning.dark">
                  {previewData.existingRows}
                </Typography>
                <Typography variant="body2" color="warning.dark">
                  Già esistenti
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light' }}>
                <Typography variant="h4" color="error.dark">
                  {previewData.errorRows}
                </Typography>
                <Typography variant="body2" color="error.dark">
                  Con errori
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Import Mode Selection - Solo se ci sono record esistenti */}
          {previewData.existingRows > 0 && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <FormControl component="fieldset">
                <FormLabel component="legend">
                  Come gestire i {previewData.existingRows} record già esistenti?
                </FormLabel>
                <RadioGroup
                  value={importMode}
                  onChange={(e) => setImportMode(e.target.value)}
                  row
                >
                  <FormControlLabel
                    value="skip"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1">Salta esistenti</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Mantieni i dati già presenti, importa solo i nuovi
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="update"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1">Aggiorna esistenti</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Sovrascrivi i dati esistenti con quelli del CSV
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Paper>
          )}

          {/* Selezione righe da importare */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1">
                Seleziona record da importare: <strong>{selectedRows.size}</strong> selezionati
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={selectAllMode ? <DeselectIcon /> : <SelectAllIcon />}
                onClick={toggleSelectAll}
              >
                {selectAllMode ? 'Deseleziona tutti' : 'Seleziona tutti'}
              </Button>
            </Box>
            
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Puoi deselezionare i record che non vuoi importare. Le righe con errori sono automaticamente escluse.
              </Typography>
            </Alert>
          </Paper>

          {/* Preview Table con checkbox */}
          {previewData.preview.length > 0 && (
            <Paper sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ p: 2 }}>
                Anteprima prime {previewData.preview.length} righe
              </Typography>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selectedRows.size > 0 && selectedRows.size < previewData.validRows}
                          checked={selectedRows.size === previewData.validRows}
                          onChange={toggleSelectAll}
                        />
                      </TableCell>
                      <TableCell>Riga</TableCell>
                      {previewData.headers.slice(0, 4).map((h) => (
                        <TableCell key={h}>{h}</TableCell>
                      ))}
                      <TableCell>Stato</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.preview.map((item) => {
                      const hasErrors = item.errors && item.errors.length > 0;
                      const isExisting = item.mapped?._exists;
                      const isSelected = selectedRows.has(item.row);
                      
                      return (
                        <TableRow 
                          key={item.row}
                          sx={{ 
                            opacity: hasErrors ? 0.6 : 1,
                            bgcolor: hasErrors ? 'error.lighter' : (isSelected ? 'action.selected' : 'inherit')
                          }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isSelected}
                              disabled={hasErrors}
                              onChange={() => toggleRowSelection(item.row)}
                            />
                          </TableCell>
                          <TableCell>{item.row}</TableCell>
                          {previewData.headers.slice(0, 4).map((h) => (
                            <TableCell key={h}>
                              {item.data[h]?.substring(0, 25) || '-'}
                              {item.data[h]?.length > 25 && '...'}
                            </TableCell>
                          ))}
                          <TableCell>
                            {hasErrors ? (
                              <Tooltip title={item.errors.join(', ')}>
                                <Chip label="Errore" size="small" color="error" />
                              </Tooltip>
                            ) : isExisting ? (
                              <Chip label="Esistente" size="small" color="warning" />
                            ) : (
                              <Chip label="Nuovo" size="small" color="success" />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Error Table */}
          {previewData.errors && previewData.errors.length > 0 && (
            <Paper sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ p: 2, color: 'error.main' }}>
                Righe con errori ({previewData.errorRows}) - Non saranno importate
              </Typography>
              <TableContainer sx={{ maxHeight: 200 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Riga</TableCell>
                      <TableCell>Errori</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.errors.slice(0, 10).map((item) => (
                      <TableRow key={item.row}>
                        <TableCell>{item.row}</TableCell>
                        <TableCell>
                          {item.errors.map((e, i) => (
                            <Chip
                              key={i}
                              label={e}
                              size="small"
                              color="error"
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                    {previewData.errors.length > 10 && (
                      <TableRow>
                        <TableCell colSpan={2}>
                          <Typography variant="body2" color="text.secondary">
                            ...e altri {previewData.errors.length - 10} errori
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}
    </Box>
  );

  // Step 3: Risultato
  const renderResult = () => (
    <Box sx={{ mt: 2, textAlign: 'center' }}>
      {importResult && (
        <>
          <SuccessIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Importazione completata!
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {entityInfo[selectedEntity]?.label || selectedEntity}
          </Typography>

          <Grid container spacing={2} sx={{ mt: 2, maxWidth: 600, mx: 'auto' }}>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, bgcolor: 'success.light' }}>
                <Typography variant="h4" color="success.dark">
                  {importResult.inserted}
                </Typography>
                <Typography variant="body2" color="success.dark">
                  Inseriti
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, bgcolor: 'info.light' }}>
                <Typography variant="h4" color="info.dark">
                  {importResult.updated}
                </Typography>
                <Typography variant="body2" color="info.dark">
                  Aggiornati
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, bgcolor: 'warning.light' }}>
                <Typography variant="h4" color="warning.dark">
                  {importResult.skipped}
                </Typography>
                <Typography variant="body2" color="warning.dark">
                  Saltati
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                <Typography variant="h4" color="error.dark">
                  {importResult.errorCount}
                </Typography>
                <Typography variant="body2" color="error.dark">
                  Errori
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {importResult.errors && importResult.errors.length > 0 && (
            <Paper sx={{ mt: 3, p: 2, textAlign: 'left', maxWidth: 600, mx: 'auto' }}>
              <Typography variant="subtitle2" color="error.main" gutterBottom>
                Errori durante l'importazione:
              </Typography>
              <Box sx={{ maxHeight: 150, overflow: 'auto' }}>
                {importResult.errors.slice(0, 20).map((e, i) => (
                  <Typography key={i} variant="body2">
                    Riga {e.row}: {e.error}
                  </Typography>
                ))}
                {importResult.errors.length > 20 && (
                  <Typography variant="body2" color="text.secondary">
                    ...e altri {importResult.errors.length - 20} errori
                  </Typography>
                )}
              </Box>
            </Paper>
          )}

          <Box sx={{ mt: 4 }}>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={handleReset}
              sx={{ mr: 2 }}
            >
              Nuova importazione
            </Button>
            {onComplete && (
              <Button variant="outlined" onClick={onComplete}>
                Chiudi
              </Button>
            )}
          </Box>
        </>
      )}
    </Box>
  );

  // Determina gli steps e il contenuto in base alla modalità
  const getCurrentSteps = () => importSource === 'exported' ? stepsExported : stepsManual;

  // Render step content
  const getStepContent = (step) => {
    if (importSource === 'exported') {
      // Modalità exported: 3 steps
      switch (step) {
        case 0:
          return renderFileUpload();
        case 1:
          return renderPreview();
        case 2:
          return renderResult();
        default:
          return null;
      }
    } else {
      // Modalità manuale: 4 steps
      switch (step) {
        case 0:
          return renderEntitySelection();
        case 1:
          return renderFileUpload();
        case 2:
          return renderPreview();
        case 3:
          return renderResult();
        default:
          return null;
      }
    }
  };

  // Se nessuna modalità selezionata, mostra la selezione
  if (!importSource) {
    return (
      <Paper sx={{ p: 3 }}>
        {renderModeSelection()}
        
        {onCancel && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button onClick={onCancel}>Annulla</Button>
          </Box>
        )}
      </Paper>
    );
  }

  const steps = getCurrentSteps();
  const isResultStep = importSource === 'exported' ? activeStep === 2 : activeStep === 3;

  return (
    <Paper sx={{ p: 3 }}>
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {getStepContent(activeStep)}

      {/* Navigation Buttons */}
      {!isResultStep && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button
            onClick={handleBack}
            startIcon={<BackIcon />}
          >
            Indietro
          </Button>
          <Box>
            {onCancel && activeStep === 0 && (
              <Button onClick={onCancel} sx={{ mr: 1 }}>
                Annulla
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!canProceed() || loading}
              endIcon={<NextIcon />}
            >
              {(importSource === 'exported' && activeStep === 1) || 
               (importSource === 'manual' && activeStep === 2)
                ? `Importa ${selectedRows.size} record`
                : 'Avanti'}
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
}
